import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { ReactComponent as DownloadIcon } from '../../assets/icons/arrow-circle-down-solid.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/arrow-right-solid.svg';
import { ReactComponent as ArrowBackIcon } from '../../assets/icons/arrow-left-solid.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/expand-arrows-alt-solid.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash-alt-solid.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/times-solid.svg';
import { Preview3DView } from './Preview3DView';
import { TailSpin } from 'react-loader-spinner';

// Simple Loading component with circular spinner
const Loading: React.FC<{ text?: string }> = ({ text }) => (
	<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
		<TailSpin color="#a855f7" height={48} width={48} />
		{text && <span style={{ fontSize: '14px', color: '#6b7280' }}>{text}</span>}
	</div>
);

// Simple ARIA-compliant button component
const ARIAButton = styled.button`
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
`;

interface ImagesContainerProps {
	hasGenerated: boolean;
	variants?: Array<{ id: number; url: string }>;
	onVariantClick?: (variantId: number) => void;
	selectedVariantId?: number;
	isActive3D?: boolean;
	isLoadingGenerate?: boolean;
	isInert?: boolean;
	onUseAsInput?: (imageUrl: string) => void;
	inputImage?: string | null;
	onClearInputImage?: () => void;
	onBackToInput: () => void;
	onDeleteVariant?: (variantId: number) => void;
}

export const ImagesContainer: React.FC<ImagesContainerProps> = ({
	hasGenerated,
	variants = [],
	onVariantClick,
	selectedVariantId,
	isActive3D = false,
	isLoadingGenerate = false,
	isInert = false,
	onUseAsInput,
	inputImage,
	onClearInputImage,
	onBackToInput,
	onDeleteVariant,
}) => {
	const { T_static } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);
	const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

	// Function to handle image download
	const handleDownload = async (imageUrl: string, variantId: number, event: React.MouseEvent) => {
		event.stopPropagation();

		try {
			const response = await fetch(imageUrl);
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `ai-generated-variant-${variantId}.png`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading image:', error);
		}
	};

	// Function to handle "Use as Input"
	const handleUseAsInput = (imageUrl: string, event: React.MouseEvent) => {
		event.stopPropagation();

		if (onUseAsInput) {
			console.log('[Use as Input] Setting image as input for next generation:', imageUrl);
			onUseAsInput(imageUrl);
		}
	};

	// Function to handle fullscreen
	const handleFullscreen = (imageUrl: string, event: React.MouseEvent) => {
		event.stopPropagation();
		setFullscreenImage(imageUrl);
	};

	// Function to handle delete
	const handleDelete = (variantId: number, event: React.MouseEvent) => {
		event.stopPropagation();

		if (onDeleteVariant) {
			onDeleteVariant(variantId);
		}
	};

	return (
		<CanvasContainer {...(isInert ? { inert: 'inert' } : {})}>
			{isLoadingGenerate ? (
				// Show loading state during generation
				<InitialState>
					<LoadingGenerationWrapper>
						<Loading text={T._('Generating AI image...')} />
					</LoadingGenerationWrapper>
				</InitialState>
			) : hasGenerated ? (
				// Show grid with generated images after generation
				<VariantsState>
					<BackToInputButtonContainer>
						<BackToInputButton onClick={onBackToInput} aria-label={T._('Back to input')}>
							<ArrowBackIcon aria-hidden='true' />
							<span>{T._('Back to input')}</span>
						</BackToInputButton>
					</BackToInputButtonContainer>

					<VariantsGrid $imageCount={variants.length}>
						{variants.map((variant, index) => (
							<VariantCard
								key={variant.id}
								onClick={() => onVariantClick?.(variant.id)}
								delay={index * 0.1}
							>
								<img src={variant.url} alt={T._('AI Generated product preview')} />

								{/* Top-right actions: Fullscreen and Delete */}
								<TopRightActions>
									<ActionButton
										onClick={(e) => handleFullscreen(variant.url, e)}
										aria-label={T._('View fullscreen')}
										title={T._('View fullscreen')}
									>
										<ExpandIcon aria-hidden='true' />
									</ActionButton>
									{onDeleteVariant && (
										<ActionButton
											onClick={(e) => handleDelete(variant.id, e)}
											aria-label={T._('Delete')}
											title={T._('Delete')}
										>
											<TrashIcon aria-hidden='true' />
										</ActionButton>
									)}
								</TopRightActions>

								{/* Download overlay (center) */}
								<DownloadOverlay>
									<ARIAButton
										onClick={(e) => handleDownload(variant.url, variant.id, e)}
										aria-label={T._('Download generated image')}
									>
										<DownloadIcon aria-hidden='true' />
									</ARIAButton>
								</DownloadOverlay>

								{/* Use as Input button (bottom) - temporarily disabled
								<UseAsInputOverlay>
									<UseAsInputButton
										onClick={(e) => handleUseAsInput(variant.url, e)}
										aria-label={T._('Use as Input')}
										title={T._('Use as Input')}
									>
										<ArrowRightIcon aria-hidden='true' />
										<span>{T._('Use as Input')}</span>
									</UseAsInputButton>
								</UseAsInputOverlay>
								*/}
							</VariantCard>
						))}
					</VariantsGrid>
				</VariantsState>
			) : inputImage ? (
				// Show input image preview when set
				<InitialState>
					<InputImageContainer>
						<InputImageTitle>{T._('Custom Input Image')}</InputImageTitle>
						<InputImageWrapper>
							<img src={inputImage} alt={T._('Input image')} />
							{onClearInputImage && (
								<ClearInputButton onClick={onClearInputImage} title={T._('Clear and return to 3D view')}>
									×
								</ClearInputButton>
							)}
						</InputImageWrapper>
						<HelpText>{T._('This image will be used as input for generation')}</HelpText>
					</InputImageContainer>
				</InitialState>
			) : (
				// Show 3D view by default
				<InitialState>
					<Preview3DViewWrapper>
						<Preview3DView />
					</Preview3DViewWrapper>
					<HelpText>{T._('Rotate the 3D view to your desired angle, then click Generate')}</HelpText>
				</InitialState>
			)}

			{/* Fullscreen Modal */}
			{fullscreenImage && (
				<FullscreenModal onClick={() => setFullscreenImage(null)}>
					<FullscreenImageWrapper>
						<img src={fullscreenImage} alt={T._('AI Generated product preview')} />
						<CloseButton
							onClick={() => setFullscreenImage(null)}
							aria-label={T._('Close')}
							title={T._('Close')}
						>
							<CloseIcon aria-hidden='true' />
						</CloseButton>
					</FullscreenImageWrapper>
				</FullscreenModal>
			)}
		</CanvasContainer>
	);
};

// Styled Components

const CanvasContainer = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0.5rem;
	min-height: 300px;
	max-height: 600px;
	overflow-y: auto;

	@media (min-width: 640px) {
		padding: 1rem;
		min-height: 500px;
	}

	@media (min-width: 1024px) {
		min-height: 550px;
	}

	@media (min-width: 1536px) {
		padding: 2rem;
	}
`;

const InitialState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	animation: fadeIn 0.4s ease-out forwards;
	width: 100%;
	min-height: 300px;

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: scale(0.95);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}
`;

const Preview3DViewWrapper = styled.div`
	width: 100%;
	height: 400px;
	max-width: 400px;
	border-radius: 1rem;
	overflow: hidden;

	@media (max-width: 640px) {
		height: 300px;
	}
`;

const HelpText = styled.p`
	color: #9ca3af;
	font-size: 14px;
	margin-top: 1rem;
	text-align: center;
	max-width: 20rem;
	padding: 0 1rem;

	@media (min-width: 640px) {
		margin-top: 1.5rem;
	}
`;

const VariantsState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	animation: slideIn 0.4s ease-out forwards;
	width: 100%;
	min-height: 300px;

	@keyframes slideIn {
		from {
			opacity: 0;
			transform: translateX(-20px);
		}
		to {
			opacity: 1;
			transform: translateX(0);
		}
	}
`;

const VariantsGrid = styled.div<{ $imageCount: number }>`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 0.5rem;
	max-width: 800px;
	width: 100%;
	padding: 0 0.5rem;

	@media (min-width: 768px) {
		grid-template-columns: ${(props) =>
			props.$imageCount < 3 ? `repeat(${props.$imageCount}, 1fr)` : 'repeat(3, 1fr)'};
		gap: 1.5rem;
	}

	@media (min-width: 1024px) {
		grid-template-columns: ${(props) =>
			props.$imageCount < 3 ? `repeat(${props.$imageCount}, 1fr)` : 'repeat(4, 1fr)'};
	}
`;

const VariantCard = styled.div<{ delay: number }>`
	width: 100%;
	max-width: 400px;
	max-height: 400px;
	min-width: 0;
	min-height: 0;
	aspect-ratio: 1;
	border-radius: 0.75rem;
	overflow: hidden;
	background: linear-gradient(to bottom right, #faf5ff, #f3e8ff);
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 2px 4px -1px rgba(0, 0, 0, 0.06);
	border: 1px solid #e5e7eb;
	cursor: pointer;
	transition: all 0.2s;
	flex-shrink: 0;
	opacity: 0;
	animation: fadeInUp 0.5s ease-out forwards;
	animation-delay: ${(props) => props.delay}s;
	position: relative;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&:hover {
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
		transform: scale(1.02);
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
`;

const DownloadOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.4);
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 1;
	pointer-events: none;

	${VariantCard}:hover & {
		opacity: 1;
		pointer-events: auto;
	}

	button {
		width: 100%;
		height: 100%;
		align-items: center;
		justify-content: center;
	}

	svg {
		width: 3rem;
		height: 3rem;
		fill: white;
		filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
		transition: transform 0.2s ease;
	}

	&:hover svg {
		transform: scale(1.1);
	}
`;

const UseAsInputOverlay = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 0.75rem;
	background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 2;
	pointer-events: none;

	${VariantCard}:hover & {
		opacity: 1;
		pointer-events: auto;
	}
`;

const UseAsInputButton = styled.button`
	background: linear-gradient(135deg, #8b5cf6, #a855f7);
	border: none;
	border-radius: 0.5rem;
	padding: 0.5rem 1rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	cursor: pointer;
	box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
	transition: all 0.3s ease;
	color: white;
	font-size: 14px;
	font-weight: 500;

	&:hover {
		transform: scale(1.05);
		box-shadow: 0 6px 16px rgba(139, 92, 246, 0.6);
	}

	svg {
		width: 16px;
		height: 16px;
		fill: white;
	}
`;

const BackToInputButtonContainer = styled.div`
	display: flex;
	justify-content: flex-start;
	width: 100%;
	max-width: 800px;
	padding: 0 1rem;
	margin-bottom: 1.5rem;
`;

const BackToInputButton = styled.button`
	display: flex;
	gap: 0.5rem;
	padding: 10px 16px;
	background-color: #ffffff;
	color: #0069d3;
	border: 1px solid #e5e7eb;
	border-radius: 0.5rem;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

	svg {
		width: 1rem;
		height: 1rem;
		fill: currentColor;
	}

	&:hover {
		background-color: #f9fafb;
		color: #00acea;
		border-color: #d1d5db;
	}
`;

const LoadingGenerationWrapper = styled.div`
	max-height: 400px;
	max-width: 400px;
	height: calc(100vw - 30px);
	width: 100%;
	gap: 1.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	animation: fadeIn 0.4s ease-out forwards;
`;

const InputImageContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	animation: fadeIn 0.4s ease-out forwards;
	width: 100%;

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: scale(0.95);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}
`;

const InputImageTitle = styled.h3`
	font-size: 16px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 1rem;
	text-align: center;
`;

const InputImageWrapper = styled.div`
	position: relative;
	width: 100%;
	max-width: 400px;
	height: auto;
	max-height: 400px;
	aspect-ratio: 1;
	border-radius: 1rem;
	overflow: hidden;
	border: 2px solid #8b5cf6;
	box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.2);

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const ClearInputButton = styled.button`
	position: absolute;
	top: 8px;
	right: 8px;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.6);
	color: white;
	border: none;
	font-size: 24px;
	line-height: 1;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;

	&:hover {
		background: rgba(0, 0, 0, 0.8);
		transform: scale(1.1);
	}
`;

const TopRightActions = styled.div`
	position: absolute;
	top: 0.5rem;
	right: 0.5rem;
	display: flex;
	gap: 0.5rem;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 3;
	pointer-events: none;

	${VariantCard}:hover & {
		opacity: 1;
		pointer-events: auto;
	}
`;

const ActionButton = styled.button`
	background: rgba(0, 0, 0, 0.6);
	border: none;
	border-radius: 0.375rem;
	padding: 0.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;

	svg {
		width: 1rem;
		height: 1rem;
		fill: white;
	}

	&:hover {
		background: rgba(0, 0, 0, 0.8);
		transform: scale(1.1);
	}
`;

const FullscreenModal = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.95);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 2rem;
	animation: fadeIn 0.2s ease-out;

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
`;

const FullscreenImageWrapper = styled.div`
	position: relative;
	max-width: 90vw;
	max-height: 90vh;
	display: flex;
	align-items: center;
	justify-content: center;

	img {
		max-width: 100%;
		max-height: 90vh;
		object-fit: contain;
		border-radius: 0.5rem;
	}
`;

const CloseButton = styled.button`
	position: absolute;
	top: -3rem;
	right: 0;
	background: rgba(255, 255, 255, 0.1);
	border: none;
	border-radius: 50%;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;

	svg {
		width: 1.25rem;
		height: 1.25rem;
		fill: white;
	}

	&:hover {
		background: rgba(255, 255, 255, 0.2);
		transform: scale(1.1);
	}
`;
